<script lang="ts">
  import { planParts, type MemberPlan } from './member';

  // THE plan cell — the members table, the reactivation queue and the
  // cancelling-soon list all render membership money through this one component, so
  // the same member can't read three different ways on three screens.
  //
  // The column carries five different facts, and Skool models two of them as
  // INDEPENDENT axes: what Stripe bills (`plan_amount_cents` + `plan_interval`) and
  // which named level they sit on (`plan_tier`). A member can have either, both, or
  // neither. Rendering whichever one happened to be non-null is what made the column
  // look like four unrelated columns stacked in one.
  //
  // So every row gets the same two-slot anatomy — VALUE on top, QUALIFIER beneath —
  // which is also the anatomy the Renewal column next door already uses:
  //
  //     $119/mo          paid
  //     $89/mo           paid AND tiered
  //     PREMIUM
  //     Comped           a level with nothing billed
  //     PREMIUM
  //     Free             no plan, and we can see the roster, so this is a fact
  //     —                no roster access here: we don't know, and won't pretend
  //
  // Two typographic rules do the rest of the work. The amount is mono/tabular and
  // its period drops to 0.65em muted, so a column of prices scans as a ladder of
  // numbers rather than a stack of strings. The non-money states are set in words,
  // not mono, because they aren't numbers — and `—` sits lighter than `Free` so
  // "unknown" can never be mistaken for "pays nothing".
  // `align` is a prop rather than a class override: passing `items-start` in a class
  // string wouldn't reliably beat the `items-end` default — which of two Tailwind
  // utilities wins is decided by their order in the stylesheet, not in the attribute.
  let {
    plan,
    align = 'right',
  }: { plan: MemberPlan; align?: 'left' | 'right' } = $props();

  // The long-form twin of admin.ts's `PLAN_INTERVAL_SUFFIX`, for the tooltip: "/mo"
  // is a column abbreviation, "every month" is a sentence. Typed as a Record rather
  // than inferred from the literal so an interval Skool adds later falls through to
  // the one-off wording instead of failing the build.
  const INTERVAL_WORDS: Record<string, string> = { month: ' every month', year: ' every year' };

  const price = $derived(planParts(plan.plan_amount_cents, plan.plan_currency, plan.plan_interval));
  // A status is the tell that a roster sweep reached this community at all.
  const known = $derived(plan.status != null);
  const priceTitle = $derived(
    price ? `${price.amount}${INTERVAL_WORDS[plan.plan_interval ?? ''] ?? ', one-off'}` : '',
  );
</script>

<div class="flex flex-col gap-0.5 leading-none {align === 'left' ? 'items-start' : 'items-end'}">
  {#if price}
    <!-- The amount is redacted; "Comped" / "Free" / "—" below are plan STATES, not
         sums, and stay sharp so a redacted table still reads as a plan column. -->
    <span class="redact font-mono text-xs tabular-nums" title={priceTitle}>
      {price.amount}{#if price.period}<span class="text-[0.65em] text-base-content/55">{price.period}</span>{/if}
    </span>
  {:else if plan.plan_tier}
    <span class="text-xs font-medium text-base-content/70" title="On a named tier with nothing billed">Comped</span>
  {:else if known}
    <span class="text-xs text-base-content/60" title="No paid plan">Free</span>
  {:else}
    <span class="font-mono text-xs text-base-content/40" title="No billing data — we aren't staff in this community">—</span>
  {/if}
  {#if plan.plan_tier}
    <!-- The tier as a classification, not a chip: it isn't clickable, and a boxed
         chip at a different height on every other row is most of what made this
         column noisy. Echoes the app's own `.eyebrow` label convention. -->
    <span
      class="font-mono text-[0.6rem] uppercase tracking-[0.1em] text-base-content/50"
      title="Named membership tier"
    >{plan.plan_tier}</span>
  {/if}
</div>
